import { create } from 'zustand';

import type { PlaybooksGenerationInputs, PlaybooksGenerationStrategy, SequenceStep, SequenceSteps } from './schemas';

/**
 * Launcher V2 U6 — per-step AI-generated-copy review state, restructured
 * from PR C QA follow-up U12B's whole-snapshot shape (a bare `steps:
 * SequenceSteps` array plus `headVersionId`) to support R8/R11/R13: steps now
 * arrive one at a time over SSE (`streamSequenceCopyGeneration`,
 * `api-client.ts`), each is independently Accept/Reject-able and editable
 * before it is, and a regenerate keeps whatever the rep already accepted
 * instead of discarding it.
 *
 * ── WHY A STORE AND NOT `useState` (unchanged from U12B) ──────────────────
 * `SequencesStep` renders `<SequenceEditorShell key={selectedRung.draftKey} …>`,
 * so clicking another rung on the rail UNMOUNTS the shell and everything under
 * it. A generation cost an LLM call and, per-step, several seconds of the
 * rep's attention — losing it to a stray rail click is worse than losing a
 * half-typed subject line, so it lives here instead of component state. Plan
 * landmine 5 names this exact case; `playbooks-copy-generation-ui.test.tsx`
 * proves it through real rail clicks (full unmounts), never a rerender.
 *
 * ── REQUEST TOKENS (unchanged from PR C QA follow-ups) ─────────────────────
 * Entries have no identity of their own beyond `${scope}|${sequenceId}` (see
 * SHAPE below), so a promise that outlives its component (a rail click
 * mid-generation) must not write into whatever now owns that slot. Every
 * async-boundary action ({@link stageGeneratedStep}, {@link
 * finishCopyGeneration}, {@link failCopyGeneration}) is told the token
 * {@link startCopyGeneration} minted and is a no-op unless the entry is STILL
 * `pending` with that EXACT token — cleared (a Play switch), promoted by an
 * earlier resolve, or replaced by a newer generation all fail the check
 * identically. {@link clearGeneratedCopy} stays unconditional — its callers
 * (Discard, and U7's "Apply to sequence") act on the entry the rep is
 * looking AT right now, never on a stale async resolve.
 *
 * ── SHAPE ─────────────────────────────────────────────────────────────────
 * One entry per (editing SCOPE, SEQUENCE) pair — {@link generatedCopyKey} —
 * scoped by the rung's `draftKey` (or the string literal `'library'`), for the
 * same D1 shared-edit-leak reason `sequence-copy-draft-store.ts` is scoped: a
 * save on a shared sequence branches PER RUNG under branch-on-edit, so a
 * generation started on rung A must never surface — or be Accept/Reject-able
 * — on rung B just because B maps the same sequence.
 *
 * `GeneratedCopyPending.steps` accumulates per-step {@link StepReview}s AS
 * THEY ARRIVE (R8 — "each step card switches into review mode as its own
 * `step` frame arrives"), so the review UI has something to show before the
 * whole generation finishes. `GeneratedCopyReview` — status `'ready'` — is the
 * same per-step map once the stream's `done` frame lands, plus the
 * `headVersionId`/`thinContext` the endpoint returned. `StepReview.unchanged`
 * is computed once, at staging time, against whatever step CURRENTLY occupied
 * that position (the caller passes it in) — a generation that reproduced a
 * step verbatim needs no rep decision and counts as reviewed
 * ({@link reviewTally}) without one.
 *
 * ── REGENERATE KEEPS ACCEPTED STEPS (R8 d15) ───────────────────────────────
 * {@link startCopyGeneration} may be called again on a `'ready'` entry (a
 * second "Generate" click) — not just on an empty slot. When it is, the new
 * `pending` entry's `steps` seeds ONLY the previously `'accepted'` reviews
 * (pass `keepAccepted = false` to drop even those); everything else — a step
 * still `'pending'`, one the rep `'rejected'`, one that came back
 * `unchanged` — is dropped and waits to be re-staged by the new run. The prior
 * `'ready'` snapshot is kept on the new pending entry's `previous` field
 * SOLELY so {@link failCopyGeneration} can restore it whole if the regenerate
 * fails — a failed second Generate must not destroy reviewed work the rep
 * already has. {@link stageGeneratedStep} additionally refuses to overwrite a
 * position already carrying an `'accepted'` decision, so even if the server
 * re-sends every position on a regenerate (a whole-snapshot generation), an
 * accepted step's `staged`/`edited` content cannot be clobbered by the new
 * run before the rep decides on it again.
 *
 * ── ACCEPT NO LONGER HANDS OFF TO THE SOURCE DRAFT STORE (decision 16) ─────
 * U12B's Accept wrote the generated snapshot into `sequence-copy-draft-store.ts`
 * as an ordinary per-step draft, so it saved through the existing per-step
 * version-write path. That handoff is deleted: per-step decisions
 * ({@link decideStagedStep}) and edits ({@link editStagedStep}) now live and
 * stay here for the whole review — U7 builds the review UI against this
 * store directly, and terra-qyt.10's "Apply to sequence" writes the approved
 * steps ({@link resolveApprovedSteps}) IN PLACE onto the CURRENT sequence's
 * head, through the host's existing gated save, never into the source
 * sequence's hand-typed drafts.
 *
 * Module-scope zustand (root CLAUDE.md "Zustand for Module-Scope Client
 * State"), same conventions as `sequence-copy-draft-store.ts`: plain
 * `create<State>(…)`, exported {@link initialState} so tests reset with
 * `useGeneratedCopyStore.setState(initialState, true)`, and actions as
 * module-level functions rather than fields on the state object.
 *
 * `PlayFlowShell` clears this store on its `[playId]` effect (top of setup AND
 * cleanup, StrictMode-resilient per that effect's own documented idiom) via
 * {@link clearGeneratedCopyReviews} — the keys carry no play identity, so two
 * Plays mapping the same Sequence would otherwise share one rep's review.
 * terra-qyt round-2 fix U18 (W1) — `PlayFlowShell`'s `handleSeedChange` ALSO
 * calls {@link clearGeneratedCopyReviews} on a playbook seed swap: a
 * generation is computed FROM one campaign's context, so it is not campaign-
 * neutral like the rung/sequence keys above imply — see that handler's own
 * doc for the full reasoning.
 */

/**
 * One step's staged generation and the rep's decision about it.
 *
 * `edited` is `null` until the rep changes the generated text — U7's B column
 * — and only ever patches `subject`/`body` (never `staged`, which stays the
 * server's own output so a discarded edit has something to revert to).
 * `unchanged` is fixed at staging time (see the module doc) and never
 * recomputed later, even if the rep edits `staged`'s sibling fields — it
 * answers "did the model change anything", not "is there anything left to
 * review right now" (that's {@link reviewTally}).
 */
export interface StepReview {
  staged: SequenceStep;
  edited: { subject?: string; body?: string } | null;
  decision: 'pending' | 'accepted' | 'rejected';
  unchanged: boolean;
}

/** A generation in flight — steps accumulate here as their SSE frames arrive. */
export interface GeneratedCopyPending {
  status: 'pending';
  /**
   * Minted by {@link startCopyGeneration}, unique per generation attempt. The
   * async handler's resolve/reject path must present this SAME token back to
   * {@link stageGeneratedStep}/{@link finishCopyGeneration}/
   * {@link failCopyGeneration} — see the module doc's "REQUEST TOKENS".
   */
  token: number;
  /** Per-step reviews staged so far, keyed by `SequenceStep.position`. */
  steps: Record<number, StepReview>;
  /** `Date.now()` at the moment this generation started — informational only, no caller reads it today. */
  startedAt: number;
  /**
   * The `'ready'` entry THIS pending run replaced, ONLY on a regenerate
   * (`startCopyGeneration` called against an existing `'ready'` entry).
   * `null` for a fresh generation. {@link failCopyGeneration} restores this
   * whole snapshot on failure — see the module doc's "REGENERATE KEEPS
   * ACCEPTED STEPS".
   */
  previous: GeneratedCopyReview | null;
  /**
   * D6 — the exact generation-request inputs THIS run started with, so the
   * "Generate with AI" drawer can restore its configure form from them on
   * reopen instead of the live (possibly since-changed) form, and so
   * `handleConfirm` (Wave C) reads this snapshot rather than the live form.
   * Optional only so pre-D6 callers/fixtures that never supplied one keep
   * working — {@link generationSnapshot} reads `undefined` as "no snapshot".
   */
  inputs?: PlaybooksGenerationInputs;
}

/** A generation whose `done` frame has landed — the rep reviews per step from here. */
export interface GeneratedCopyReview {
  status: 'ready';
  /**
   * The sequence head this generation was computed against. NOT used as any
   * save's `expectedVersionId` — only to warn the rep when the head has
   * moved since they pressed Generate (`CopyDiffView`'s `isHeadStale`).
   */
  headVersionId: string | null;
  /** Whether the campaign description was thin enough to warn the rep (`isThinDescription`, U4b). */
  thinContext: boolean;
  /**
   * Which strategy actually ran (U8b) — the `done` frame's own `strategy`
   * field, carried forward so `GenerateWithAIDrawer.tsx`'s Apply write can
   * report the REAL value in `generationInputs.strategy` rather than
   * re-guessing `DEFAULT_GENERATION_STRATEGY`. Optional (not backfilled onto
   * every historical caller/fixture in this codebase) — a `finishCopyGeneration`
   * that never got this argument, or a manually-constructed `'ready'` entry
   * in an older test, is missing it; the drawer falls back to
   * `DEFAULT_GENERATION_STRATEGY` (`copy-generation-rules.ts`) for that case.
   */
  strategy?: PlaybooksGenerationStrategy;
  steps: Record<number, StepReview>;
  /** D6 — carried forward from the pending entry's own {@link GeneratedCopyPending.inputs}; see that field's doc. */
  inputs?: PlaybooksGenerationInputs;
}

/**
 * A generation whose `done` frame landed but did not cover every position it
 * claimed to (D5) — the transport (JSON fallback or a dropped SSE frame)
 * delivered fewer `step` frames than the `done` frame's own `positions` list.
 * Refusing the `'ready'` transition here means the review UI never shows a
 * generation as complete when a position silently never arrived; the rep's
 * only path forward is Discard/retry, same as any other failed generation.
 */
export interface GeneratedCopyErrorEntry {
  status: 'error';
  /** Rep-facing refusal text — see {@link finishCopyGeneration}'s own doc. */
  detail: string;
}

export type GeneratedCopyEntry = GeneratedCopyPending | GeneratedCopyReview | GeneratedCopyErrorEntry;

interface GeneratedCopyState {
  /** Keyed by {@link generatedCopyKey}. A missing key means "no generation here". */
  entries: Record<string, GeneratedCopyEntry>;
}

/** Exported per the store convention — reset idiom is `useGeneratedCopyStore.setState(initialState, true)`. */
export const initialState: GeneratedCopyState = {
  entries: {},
};

export const useGeneratedCopyStore = create<GeneratedCopyState>(() => ({ ...initialState }));

/** Monotonic source for {@link GeneratedCopyPending.token} — see the module doc's "REQUEST TOKENS". */
let nextGenerationToken = 0;

/**
 * The entity key for one sequence's generation: `${scope}|${sequenceId}`.
 *
 * `scope` is the editing SURFACE, not the sequence — a rung's `draftKey`
 * (`playbook-draft.ts`'s `rungDraftKey()`/`new:N`) in the play flow, or the
 * string literal `'library'` on the Config page. Same `|` separator and same
 * reasoning as `sequence-copy-draft-store.ts`'s `copyDraftKey` — rung scope
 * values already contain `:` (`rung:<id>`, `new:<n>`), so `:` cannot be the
 * separator here either.
 */
export function generatedCopyKey(scope: string, sequenceId: string): string {
  return `${scope}|${sequenceId}`;
}

/** Only the `'accepted'` reviews of a ready entry — what a regenerate keeps by default. */
function acceptedStepsOf(entry: GeneratedCopyReview): Record<number, StepReview> {
  const kept: Record<number, StepReview> = {};
  for (const [positionKey, review] of Object.entries(entry.steps)) {
    if (review.decision === 'accepted') {
      kept[Number(positionKey)] = review;
    }
  }
  return kept;
}

/**
 * Marks a sequence's generation as in flight, and returns the token THIS call
 * minted for it — or `null` if it refused to start one.
 *
 * Refuses (returns `null`) only when a generation is ALREADY `'pending'` for
 * this key — the Generate button is disabled while pending, so this is
 * defense in depth rather than the primary guard, but it is the only check
 * that holds across the unmount/remount this store exists to survive.
 *
 * Unlike U12B, this is ALLOWED to start against an existing `'ready'` entry —
 * a regenerate. `keepAccepted` (default `true`) controls whether the new
 * pending entry's `steps` seeds from the prior entry's `'accepted'` reviews
 * (see the module doc's "REGENERATE KEEPS ACCEPTED STEPS"); every other prior
 * review — `'pending'`, `'rejected'`, or `unchanged` — is dropped either way
 * and waits to be re-staged. The prior `'ready'` entry itself is kept on the
 * new pending entry's `previous` field so a failed regenerate can restore it
 * whole.
 *
 * `inputs` (D6) — when supplied, snapshotted onto the new pending entry (see
 * {@link GeneratedCopyPending.inputs}) and carried forward to `'ready'` by
 * {@link finishCopyGeneration}. Optional so pre-D6 callers keep working.
 */
export function startCopyGeneration(
  scope: string,
  sequenceId: string,
  keepAccepted = true,
  inputs?: PlaybooksGenerationInputs,
): number | null {
  let token: number | null = null;
  const key = generatedCopyKey(scope, sequenceId);
  useGeneratedCopyStore.setState((state) => {
    const existing = state.entries[key];
    if (existing?.status === 'pending') return state;
    token = ++nextGenerationToken;
    const previous = existing?.status === 'ready' ? existing : null;
    const steps = previous && keepAccepted ? acceptedStepsOf(previous) : {};
    const pending: GeneratedCopyPending = {
      status: 'pending',
      token,
      steps,
      startedAt: Date.now(),
      previous,
      inputs,
    };
    return { entries: { ...state.entries, [key]: pending } };
  });
  return token;
}

/**
 * Stages one step's generated content as its SSE `step` frame arrives — ONLY
 * if `token` still matches the pending entry currently in that slot (see the
 * module doc's "REQUEST TOKENS"; a mismatch means a newer generation, a
 * Play-switch clear, or an already-settled request, and the frame is
 * silently dropped).
 *
 * `currentStep` is the position's step as it stands RIGHT NOW (the caller's
 * own read of the sequence head at staging time) — used only to compute
 * {@link StepReview.unchanged}; it is never stored.
 *
 * A position that already carries an `'accepted'` decision (kept across a
 * regenerate by {@link startCopyGeneration}) is left untouched — see the
 * module doc's "REGENERATE KEEPS ACCEPTED STEPS".
 */
export function stageGeneratedStep(
  scope: string,
  sequenceId: string,
  token: number,
  step: SequenceStep,
  currentStep: SequenceStep | undefined,
): void {
  const key = generatedCopyKey(scope, sequenceId);
  useGeneratedCopyStore.setState((state) => {
    const entry = state.entries[key];
    if (entry === undefined || entry.status !== 'pending' || entry.token !== token) return state;
    if (entry.steps[step.position]?.decision === 'accepted') return state;
    const unchanged =
      (step.subject ?? '') === (currentStep?.subject ?? '') && (step.body ?? '') === (currentStep?.body ?? '');
    const review: StepReview = { staged: step, edited: null, decision: 'pending', unchanged };
    return {
      entries: {
        ...state.entries,
        [key]: { ...entry, steps: { ...entry.steps, [step.position]: review } },
      },
    };
  });
}

/** Rep-facing detail on a {@link GeneratedCopyErrorEntry} produced by an incomplete generation — see {@link finishCopyGeneration}. */
export const GENERATION_INCOMPLETE_DETAIL = 'Generation finished incomplete — retry';

/**
 * Promotes a pending generation to `'ready'` on its stream's `done` frame —
 * ONLY if `token` still matches (see "REQUEST TOKENS"). Carries forward
 * whatever `steps` had accumulated (staged frames plus any kept-accepted
 * reviews); nothing here re-derives them.
 *
 * D19 — `positions` (the `done` frame's own list of positions the server
 * generated for — both the SSE frame and the JSON-fallback's synthesized one
 * always carry it, `schemas.ts`'s `PlaybooksGenerateProgressEventSchema`)
 * is now REQUIRED, not optional (D5's original shipped shape). Every one of
 * those positions must already have a staged {@link StepReview} or the
 * `'ready'` transition is REFUSED:
 *   - a FRESH generation (no `previous` on the pending entry) becomes a
 *     {@link GeneratedCopyErrorEntry} — there is no prior review to fall back
 *     to, so the review UI shows the actionable error stage instead of ever
 *     exposing "Apply to sequence" on an empty/partial review.
 *   - an incomplete REGENERATE (`previous` set) instead RESTORES that prior
 *     `'ready'` entry WHOLE — same as {@link failCopyGeneration}'s own
 *     failure path — so an incomplete second Generate never destroys
 *     decisions (accepted/rejected/edited) the rep already made on the first
 *     review (Codex NEW-INCOMPLETE-REGENERATE-LOSES-REVIEW). The caller
 *     still learns the run was incomplete via this function's `complete: false`
 *     return so it can surface the retry detail as a banner alongside the
 *     restored review, rather than silently swallowing the failure.
 *
 * D20 — on a CLEAN (complete) promotion, the pending entry's own `inputs`
 * snapshot (if any, D6) is FINALIZED here: `sourceHeadVersionId` and
 * `strategy` — placeholders `startCopyGeneration` could not know at
 * Generate-click time — are replaced with the run's actual values.
 * `generatedAt` is left untouched (it is the moment Generate was clicked,
 * not the moment it finished). From this point on the snapshot is the ONLY
 * source `handleConfirm` (Wave C) reads for the confirm write — never the
 * live form.
 *
 * Returns `{ current, complete }`: `current` is `false` for a stale/no-op
 * call (nothing here to act on, mirrors {@link failCopyGeneration}'s own
 * `wasCurrent`); `complete` is `false` when the completeness gate refused
 * (whether or not `previous` existed to restore) — a still-current caller
 * uses this to decide whether to show the incomplete-generation banner.
 */
export function finishCopyGeneration(
  scope: string,
  sequenceId: string,
  token: number,
  result: {
    headVersionId: string | null;
    thinContext: boolean;
    /** U8b — see `GeneratedCopyReview.strategy`'s own doc for why this is optional. */
    strategy?: PlaybooksGenerationStrategy;
    /** D19 — see this function's own doc; every entry here must already have a staged {@link StepReview}. */
    positions: number[];
  },
): { current: boolean; complete: boolean } {
  const key = generatedCopyKey(scope, sequenceId);
  let current = false;
  let complete = true;
  useGeneratedCopyStore.setState((state) => {
    const entry = state.entries[key];
    if (entry === undefined || entry.status !== 'pending' || entry.token !== token) return state;
    current = true;
    const missing = result.positions.some((position) => entry.steps[position] === undefined);
    if (missing) {
      complete = false;
      if (entry.previous !== null) {
        return { entries: { ...state.entries, [key]: entry.previous } };
      }
      const errored: GeneratedCopyErrorEntry = { status: 'error', detail: GENERATION_INCOMPLETE_DETAIL };
      return { entries: { ...state.entries, [key]: errored } };
    }
    const inputs =
      entry.inputs === undefined
        ? undefined
        : { ...entry.inputs, sourceHeadVersionId: result.headVersionId, strategy: result.strategy ?? entry.inputs.strategy };
    const ready: GeneratedCopyReview = {
      status: 'ready',
      headVersionId: result.headVersionId,
      thinContext: result.thinContext,
      strategy: result.strategy,
      steps: entry.steps,
      inputs,
    };
    return { entries: { ...state.entries, [key]: ready } };
  });
  return { current, complete };
}

/**
 * The failure counterpart to {@link finishCopyGeneration} — ONLY acts if
 * `token` still matches the pending entry, and reports whether it did. A
 * caller whose request turns out to be stale (return `false`) must not show a
 * rep-facing error toast for it — the entry it would be complaining about
 * generating either belongs to a newer request already in flight, or has
 * already resolved into a review the rep may be looking at right now.
 *
 * On a still-current failure: if this was a REGENERATE (the pending entry's
 * `previous` is set), the prior `'ready'` entry is restored WHOLE — a failed
 * second Generate must not destroy reviewed work the rep already had (see the
 * module doc's "REGENERATE KEEPS ACCEPTED STEPS"). Otherwise (a fresh
 * generation with nothing to fall back to) the entry is dropped entirely,
 * same as U12B.
 */
export function failCopyGeneration(scope: string, sequenceId: string, token: number): boolean {
  let cleared = false;
  const key = generatedCopyKey(scope, sequenceId);
  useGeneratedCopyStore.setState((state) => {
    const entry = state.entries[key];
    if (entry === undefined || entry.status !== 'pending' || entry.token !== token) return state;
    cleared = true;
    if (entry.previous !== null) {
      return { entries: { ...state.entries, [key]: entry.previous } };
    }
    const { [key]: _dropped, ...rest } = state.entries;
    return { entries: rest };
  });
  return cleared;
}

/**
 * Patches a staged step's edited text on a `'ready'` entry. A no-op while the
 * entry is `'pending'` (nothing to edit yet — the step hasn't arrived, or the
 * rep is editing a review that no longer exists) and a no-op for a position
 * the entry has no review for. `patch` merges onto any existing `edited`
 * value (a rep may edit subject, then body, without losing the first edit).
 */
export function editStagedStep(
  scope: string,
  sequenceId: string,
  position: number,
  patch: { subject?: string; body?: string },
): void {
  const key = generatedCopyKey(scope, sequenceId);
  useGeneratedCopyStore.setState((state) => {
    const entry = state.entries[key];
    if (entry === undefined || entry.status !== 'ready') return state;
    const review = entry.steps[position];
    if (review === undefined) return state;
    const edited = { ...(review.edited ?? {}), ...patch };
    return {
      entries: {
        ...state.entries,
        [key]: { ...entry, steps: { ...entry.steps, [position]: { ...review, edited } } },
      },
    };
  });
}

/**
 * Records the rep's Accept/Reject/Undo decision on one staged step. A no-op
 * while the entry is `'pending'` and a no-op for a position the entry has no
 * review for (an unknown position, or a step the entry never staged).
 */
export function decideStagedStep(
  scope: string,
  sequenceId: string,
  position: number,
  decision: StepReview['decision'],
): void {
  const key = generatedCopyKey(scope, sequenceId);
  useGeneratedCopyStore.setState((state) => {
    const entry = state.entries[key];
    if (entry === undefined || entry.status !== 'ready') return state;
    const review = entry.steps[position];
    if (review === undefined) return state;
    return {
      entries: {
        ...state.entries,
        [key]: { ...entry, steps: { ...entry.steps, [position]: { ...review, decision } } },
      },
    };
  });
}

/**
 * Drops a sequence's entry unconditionally — the exit for Discard and (U8)
 * "Apply to sequence" once it has read what it needs via
 * {@link resolveApprovedSteps}. Unconditional (no token check) is correct
 * here specifically because these are REP-INITIATED actions on the entry
 * currently rendered on screen, not an async resolve/reject that might be
 * settling long after something else took its place — the failure path that
 * DOES need a token check is {@link failCopyGeneration}.
 */
export function clearGeneratedCopy(scope: string, sequenceId: string): void {
  const key = generatedCopyKey(scope, sequenceId);
  useGeneratedCopyStore.setState((state) => {
    if (state.entries[key] === undefined) return state;
    const { [key]: _dropped, ...rest } = state.entries;
    return { entries: rest };
  });
}

/**
 * Codex fixes 2026-09-17, F2 — puts a snapshotted `'ready'` review back under
 * its key ONLY when nothing is there. The one caller is
 * `usage-sidebar-resume.ts`, re-hydrating the review the usage sidebar's
 * pending carried across a flow change that (correctly) wiped this store. A
 * present entry wins unconditionally: it is at least as new as the snapshot
 * and may hold Accept/Reject decisions the rep made while the sidebar was
 * open. Never called from an effect cleanup or a clear path.
 */
export function restoreGeneratedCopyReview(scope: string, sequenceId: string, review: GeneratedCopyReview): void {
  const key = generatedCopyKey(scope, sequenceId);
  useGeneratedCopyStore.setState((state) =>
    state.entries[key] === undefined ? { entries: { ...state.entries, [key]: review } } : state,
  );
}

/**
 * Codex F1 — drops every SCOPE's generated review for one sequence. The
 * sibling of `sequence-copy-draft-store.ts#clearCopyDraftsForSequence`,
 * called from the same place for the same reason: a Gong flow rebind rewrites
 * the sequence's SHARED head, so every scope's reviews for it go stale — a
 * review generated against the old head no longer matches what Apply would
 * write — not just the one rung the rep happened to be looking at.
 *
 * Matches the SEQUENCE segment of {@link generatedCopyKey} exactly
 * (`${scope}|${sequenceId}`, two segments), never a prefix: a sequence id that
 * is a prefix of another must not take its neighbours with it.
 */
export function clearGeneratedCopyForSequence(sequenceId: string): void {
  useGeneratedCopyStore.setState((state) => {
    const keys = Object.keys(state.entries).filter((key) => {
      const parts = key.split('|');
      return parts.length === 2 && parts[1] === sequenceId;
    });
    if (keys.length === 0) return state;
    const entries = { ...state.entries };
    for (const key of keys) delete entries[key];
    return { entries };
  });
}

/**
 * Plan 2026-09-28-006 U3a (rulebook R3/R7; replaces plan 2026-09-27-007 U4's
 * unscoped `rekeyGeneratedCopyForSequence`) — the sibling of
 * `sequence-copy-draft-store.ts#rekeyCopyDraftsForRung`, called from the same
 * place (`ensure-play-owned.ts#adoptPlayLadderMoves`): an in-play fork moved ONE
 * group (editing scope `scope`) onto the play's copy `toId`, so that scope's
 * review for `fromId` moves with it; every other scope's review stays under
 * `fromId`, which that group still addresses. Entries move BY REFERENCE (a
 * pending entry keeps its token). Matches the scope and sequence segments
 * exactly. A no-op (same state object) when nothing matches or `fromId ===
 * toId`.
 */
export function rekeyGeneratedCopyForRung(scope: string, fromId: string, toId: string): void {
  if (fromId === toId) return;
  useGeneratedCopyStore.setState((state) => {
    let changed = false;
    const entries: Record<string, GeneratedCopyEntry> = {};
    for (const [key, value] of Object.entries(state.entries)) {
      const parts = key.split('|');
      if (parts.length === 2 && parts[0] === scope && parts[1] === fromId) {
        entries[generatedCopyKey(scope, toId)] = value;
        changed = true;
      } else {
        entries[key] = value;
      }
    }
    return changed ? { entries } : state;
  });
}

/** Drops every review — `PlayFlowShell`'s `[playId]` reset (see the module doc). */
export function clearGeneratedCopyReviews(): void {
  useGeneratedCopyStore.setState({ entries: {} });
}

/**
 * Codex review of Playbooks QA round 5 (finding B1) — the scoped twin of
 * {@link clearGeneratedCopyReviews}, for a "Discard changes" action that
 * must only ever destroy ITS OWN editing surface's generations. Prefix-
 * matches on `${scope}|`, the same convention {@link generatedCopyKey}'s
 * callers and `unsaved-guard.ts`'s scoped selectors use. See
 * `sequence-copy-draft-store.ts#clearCopyDraftsInScope`'s doc for the full
 * rationale (both stores gained this the same day, same reason).
 */
export function clearGeneratedCopyReviewsInScope(scope: string): void {
  useGeneratedCopyStore.setState((state) => {
    const prefix = `${scope}|`;
    const keys = Object.keys(state.entries).filter((key) => key.startsWith(prefix));
    if (keys.length === 0) return state;
    const entries = { ...state.entries };
    for (const key of keys) delete entries[key];
    return { entries };
  });
}

/**
 * Selector factory for one sequence's entry.
 *
 * Returns the stored object by reference (or `undefined`), so a subscribed
 * editor shell re-renders only when ITS sequence's review changes.
 */
export function selectGeneratedCopyEntry(
  scope: string,
  sequenceId: string | null | undefined,
): (state: GeneratedCopyState) => GeneratedCopyEntry | undefined {
  return (state) => (sequenceId ? state.entries[generatedCopyKey(scope, sequenceId)] : undefined);
}

/**
 * D6 — the generation-request snapshot for one sequence's entry, `null` when
 * there is no entry, the entry is a {@link GeneratedCopyErrorEntry} (nothing
 * to restore from a failed run), or the entry predates D6 and never got one.
 * `generate-with-ai-store.ts`'s `openGenerateSession` reads this to restore
 * its configure form on reopen instead of reseeding from scratch.
 */
export function generationSnapshot(
  scope: string,
  sequenceId: string,
): (state: GeneratedCopyState) => PlaybooksGenerationInputs | null {
  return (state) => {
    const entry = state.entries[generatedCopyKey(scope, sequenceId)];
    if (entry === undefined || entry.status === 'error') return null;
    return entry.inputs ?? null;
  };
}

/**
 * How much of an entry's review is done. A step counts as reviewed once it
 * has a non-`'pending'` decision OR is `unchanged` (a step the model
 * reproduced verbatim needs no rep decision — see the module doc's SHAPE
 * section). `undefined` (no entry at all) reads as a vacuously-complete empty
 * review, matching a footer that has nothing to gate. A {@link
 * GeneratedCopyErrorEntry} (D5) has no steps to tally and reads the same way.
 *
 * `pending` (terra-qyt.10's Data Contract) is `total - reviewed` — the
 * drawer's "Apply to sequence" footer button gates on `pending === 0` rather
 * than `allReviewed` directly; both express the same condition, `pending` is
 * the shape the Codex-authored consumer contract names.
 */
export function reviewTally(entry: GeneratedCopyEntry | undefined): {
  reviewed: number;
  total: number;
  pending: number;
  allReviewed: boolean;
} {
  if (entry === undefined || entry.status === 'error') {
    return { reviewed: 0, total: 0, pending: 0, allReviewed: true };
  }
  const reviews = Object.values(entry.steps);
  const total = reviews.length;
  const reviewed = reviews.filter((review) => review.unchanged || review.decision !== 'pending').length;
  return { reviewed, total, pending: total - reviewed, allReviewed: reviewed === total };
}

/**
 * Positions still awaiting a rep decision (excludes `unchanged` steps, which
 * need none), ascending. A {@link GeneratedCopyErrorEntry} (D5) has no steps
 * at all — empty.
 */
export function unreviewedPositions(entry: GeneratedCopyEntry): number[] {
  if (entry.status === 'error') return [];
  return Object.values(entry.steps)
    .filter((review) => !review.unchanged && review.decision === 'pending')
    .map((review) => review.staged.position)
    .sort((a, b) => a - b);
}

/**
 * The steps a "Apply to sequence" write should actually save: `headSteps`
 * with every `'accepted'` position's text replaced by the rep's edit (or, if
 * unedited, the model's own staged text), `copyStatus` forced to `'draft'`.
 * Every other head position — no review at all, `'pending'`, `'rejected'`,
 * `unchanged` — passes through VERBATIM.
 *
 * Iterates `headSteps`, not `entry.steps` — a staged position with no
 * matching head step (the head lost a step between Generate and this call)
 * is silently skipped rather than invented into the output. A {@link
 * GeneratedCopyErrorEntry} (D5) has no accepted steps at all — `headSteps`
 * passes through verbatim, same as an entry with nothing accepted.
 */
export function resolveApprovedSteps(
  entry: GeneratedCopyEntry,
  headSteps: readonly SequenceStep[],
): SequenceSteps {
  if (entry.status === 'error') return [...headSteps];
  return headSteps.map((head) => {
    const review = entry.steps[head.position];
    if (review === undefined || review.decision !== 'accepted') return head;
    return {
      ...head,
      subject: review.edited?.subject ?? review.staged.subject,
      body: review.edited?.body ?? review.staged.body,
      copyStatus: 'draft',
    };
  });
}
